import { Download } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import CreateForm from '../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from '../../../../../../../context/ApplicationContext';
import { ANTRAG_TYP, ART_DES_ANTRAGS, EINREICHUNG_DER_UNTERLAGEN, PLATZHALTER_TEXT } from '../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../lib/types/types';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';
import { DownloadStammdatenBlatt } from '../../../../DownloadAntragPDF';

// import schemas
import { getAntragstellerName } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung, enumT } from 'src/utils/helper';
import AntragSchema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const StammdatenblattGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleDownloadStammdatenBlatt = () => {
		DownloadStammdatenBlatt(application, readOnly, saveApplication);
	};
	return (
		<>
			<ApplicationHeading title='Download Stammdatenblatt' description={PLATZHALTER_TEXT} />
			<Typography>
				<strong>Das ausgefüllte und unterschriebene Stammdatenblatt ist Bestandteil der Antragsunterlagen.</strong> Bitte laden Sie hier das für
				Sie vorbereitete Stammdatenblatt herunter:
			</Typography>

			<Box sx={{ mt: '1rem' }}>
				<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
					<Button variant='outlined' startIcon={<Download />} onClick={() => handleDownloadStammdatenBlatt()} sx={{ maxWidth: 'fit-content' }}>
						Stammdatenblatt {getAntragstellerName(application)} herunterladen
					</Button>
					<Typography>
						<strong>Bitte beachten Sie:</strong> Die Unterzeichnung des Stammdatenblatts muss per eigenhändiger Unterschrift erfolgen. Das
						Einfügen einer Bilddatei o.Ä. als Unterschrift ist nicht zulässig. Bitte speichern Sie das unterzeichnete Dokument anschließend als
						pdf-Datei ab und senden Sie uns das unterschriebene Original zusätzlich per Briefpost zu.
					</Typography>
				</Stack>

				<InhaltTrenner />

				<Stack spacing={3} sx={{ width: '100%', mt: '1rem' }}>
					<Typography variant='h4'>Upload Stammdatenblatt</Typography>

					<Typography>
						Bitte laden Sie hier Ihr unterzeichnetes Stammdatenblatt (pdf-Datei) als Vorab-Information über das Upload-Feld hoch:
					</Typography>
					<CreateForm
						formStruc={formStrucStammDatenblatt()}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
						size='small'
					/>
					<Typography>
						<strong>Wichtig:</strong> Das unterzeichnete Stammdatenblatt ist Bestandteil des Förderantrags. Der Upload der Erklärung an dieser
						Stelle ist daher wichtig für die Einreichung Ihres vollständigen Antrags auf Förderung.
					</Typography>
				</Stack>

				<PostVersandInformation application={application} />
			</Box>
		</>
	);
};

export default StammdatenblattGlobal;

const formStrucStammDatenblatt = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Allgemein_Eingereichtes_Stammdatenblatt, allowedFileNameKeywords: ['Stammdatenblatt'], hideLabel: true }],
		},
	];
	return struc;
};

export const getFormStrucStammdatenblattGlobal = () => {
	return concat(formStrucStammDatenblatt());
};

const PostVersandInformation: FC<{ application: any }> = ({ application }) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');
	const EinreichungDerUnterlagen = get(application, P.Foerderprogramm.schema.attributes.Konfiguration_Einreichung_Der_Antragsunterlagen.path, '');
	const antragTyp = get(application, P.Allgemein_Typ.path, '');
	const isSkizze = antragTyp === ANTRAG_TYP.Skizze;
	const isFormantrag = get(application, P.Allgemein_Art_Des_Antrags.path, '') === ART_DES_ANTRAGS.Formantrag;

	if (EinreichungDerUnterlagen === EINREICHUNG_DER_UNTERLAGEN.digital) {
		return (
			<>
				<InhaltTrenner />
				<InhaltWrapper>
					<Typography variant={'h4'} component={'p'} sx={{ fontWeight: 600 }}>
						Postversand des Stammdatenblattes
					</Typography>
					<Typography>Bitte senden Sie uns das unterzeichnete Stammdatenblatt im Original per Post zu.</Typography>
					<Typography sx={{ fontWeight: 600 }}>Postanschrift:</Typography>
					<Box>
						<Typography>HA Hessen Agentur GmbH</Typography>
						<Typography>Innovationsförderung Hessen</Typography>
						<Typography>{enumT(antragsFormular)}</Typography>
						<Typography>Mainzer Str. 118</Typography>
						<Typography>65189 Wiesbaden</Typography>
					</Box>
					<Typography>
						<strong>Nicht vergessen:</strong> Für die fristgerechte Antragseinreichung muss das{' '}
						<u>Stammdatenblatt im Original bei uns im Haus</u> vorliegen.
					</Typography>
				</InhaltWrapper>
			</>
		);
	}
	if (EinreichungDerUnterlagen === EINREICHUNG_DER_UNTERLAGEN.postalisch) {
		return (
			<>
				<InhaltTrenner />
				<InhaltWrapper>
					<Typography variant={'h4'} component={'p'} sx={{ fontWeight: 600 }}>
						Einreichung des Stammdatenblatts
					</Typography>
					<Typography>
						Das Stammdatenblatt ist Bestandteil des Förderantrags. Bitte lassen Sie uns dieses - zusammen mit den weiteren Antragsdokumenten -
						fristgerecht im Original zukommen. Weitere Informationen finden Sie im Menüpunkt{' '}
						{isSkizze ?
							'„Skizze einreichen“'
						: isFormantrag ?
							'„Formantrag einreichen“'
						:	'„Antragseinreichung“'}
						.
					</Typography>
					{/**
					 * BITTE ZUERST LESEN
					 * Laut das Ticket HA-2318 soll diese Texte komplett geändert bzw. gekürzt.
					 * Aber es kann sein, dass HA wieder die Ursprungstexte haben möchte, deswegen habe ich die Texte nicht gelöscht, sondern auskommentiert.
					 */}
					{/* <Typography sx={{ fontWeight: 600 }}>Postanschrift:</Typography>
					<Box>
						<Typography>HA Hessen Agentur GmbH</Typography>
						<Typography>Innovationsförderung Hessen</Typography>
						<Typography>{enumT(antragsFormular)}</Typography>
						<Typography>Mainzer Str. 118</Typography>
						<Typography>65189 Wiesbaden</Typography>
					</Box>
					<Typography>
						<strong>Nicht vergessen:</strong> Für die fristgerechte Antragseinreichung müssen{' '}
						<u>alle Antragsdokumente im Original bei uns im Haus</u> vorliegen.
					</Typography> */}
				</InhaltWrapper>
			</>
		);
	}

	return <></>;
};
